const BASE = '/api/wallet';

// Todas las rutas /api/wallet exigen sesión de Clerk (salvo /health). El token
// se toma de forma imperativa (window.Clerk), igual que services/yoolpay/
// chargesApi.js, para no tener que pasar getToken a cada pantalla. Si quien
// llama ya trae un token (p. ej. mensajes con useAuth().getToken), se usa ese.
async function clerkToken() {
  try {
    return (await window?.Clerk?.session?.getToken?.()) || null;
  } catch {
    return null;
  }
}

async function req(path, options = {}) {
  const { token: tokenDado, ...rest } = options;
  const token = tokenDado || (await clerkToken());
  const res = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...rest,
    body: rest.body ? JSON.stringify(rest.body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw Object.assign(new Error(err.error || `Error ${res.status}`), { status: res.status });
  }
  return res.json();
}

export const walletService = {
  getRamos: () => req('/ramos'),

  searchPolicies: (q) => req(`/policies/search?${new URLSearchParams({ q }).toString()}`),

  listPasses: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req(`/passes${qs ? `?${qs}` : ''}`);
  },

  getPass: (policyId) => req(`/passes/${policyId}`),

  getGoogleJwt: (policyId) => req(`/passes/${policyId}/google-jwt`),

  // URL de descarga del .pkpass. Se pide con fetch (lleva la sesión) en vez de
  // abrir /download directo, porque una pestaña nueva no manda el header.
  getDownloadUrl: (serial) =>
    req(`/passes/${encodeURIComponent(serial)}/download?format=json`),

  getCuotas: (policyId) => req(`/cuotas/${policyId}`),

  resyncQuotas: () => req('/resync-quotas', { method: 'POST' }),

  revokePass: (policyId) => req(`/passes/${policyId}`, { method: 'DELETE' }),

  sendPush: (policyId, reason) =>
    req(`/passes/${policyId}/push`, { method: 'POST', body: { reason } }),

  issuePass: (policyId, provider, policyData = null, emailTo = null) =>
    req('/issue', {
      method: 'POST',
      body: {
        policy_id: policyId,
        provider,
        ...(policyData ? { policy_data: policyData } : {}),
        ...(emailTo ? { email: emailTo, holder_name: policyData?.holder_name } : {}),
      },
    }),

  getBackConfig: (polizaColectivaId) =>
    req(`/back-config/${polizaColectivaId}`),

  saveBackConfig: (polizaColectivaId, backConfig) =>
    req(`/back-config/${polizaColectivaId}`, {
      method: 'PUT',
      body: { back_config: backConfig },
    }),

  // Nivel PLAN del mismo reverso. Se mezcla con los defaults de la compañía y
  // con los de la póliza colectiva, clave por clave, al armar el snapshot.
  getPlanBackConfig: (planId) =>
    req(`/back-config/plan/${planId}`),

  savePlanBackConfig: (planId, backConfig) =>
    req(`/back-config/plan/${planId}`, {
      method: 'PUT',
      body: { back_config: backConfig },
    }),

  listEvents: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req(`/events${qs ? `?${qs}` : ''}`);
  },

  getHealth: () => req('/health'),

  testIssue: (body) => req('/test/issue', { method: 'POST', body }),

  testEvent: (body) => req('/test/event', { method: 'POST', body }),

  simulateEvent: (eventType, policyId, payload = {}) =>
    req('/simulate-event', { method: 'POST', body: { event_type: eventType, policy_id: policyId, payload } }),

  broadcastEvent: (eventType, dryRun = false, filters = {}) =>
    req('/broadcast', { method: 'POST', body: { event_type: eventType, dry_run: dryRun, filters } }),

  getSyncStatus: () => req('/sync-status'),

  reconcile: () => req('/reconcile', { method: 'POST' }),
};
